/**
 * ProgressReportModal
 *
 * The end-of-program report a coach writes to a player when submitting
 * the final training-program lesson. Renders the four questions in a
 * standard white modal surface and submits BOTH the report and the
 * lesson together via a single `onSubmit` callback.
 *
 * Controlled component: the four field values + their setters live in
 * the parent (LessonVideoEditor) so the draft survives the modal
 * closing and reopening within the same editor session. No localStorage,
 * no backend persistence — session state only.
 *
 * Submit gate: ALL FOUR fields must have non-whitespace content. This is
 * an intentional divergence from the existing /orders write path's 3-of-4
 * gate (CoachProgressReport.tsx is unchanged) — the lesson-4 submission
 * is the retention moment so we hold it to a higher bar.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { capName } from '../utils/capName';

interface ReportDraft {
  whatImproved: string;
  keepDoing: string;
  nextFocus: string;
  personalNote: string;
}

interface ProgressReportModalProps {
  /** Player first name; falls back to "player" when empty/undefined. Used in
   *  question text + headline + helper copy. Always run through `capName`
   *  when interpolated so the rendered string is always title-cased. */
  playerFirstName: string;
  /** Total lessons in the program — drives the journey strip dot count. */
  totalLessons: number;
  /** Player's stated goal (from playerProfile.goal). Surfaced as a small
   *  reference card above the questions when present. Card is hidden when
   *  absent — no fallback copy. */
  playerGoal?: string;

  /** Lifted draft state owned by the parent. */
  draft: ReportDraft;
  onDraftChange: (next: ReportDraft) => void;

  /** Close without persisting to backend (X / Esc / Save draft). The
   *  parent leaves draft state intact so reopening rehydrates. */
  onClose: () => void;
  /** Fired when the coach hits "Submit Report & Lesson". The parent owns
   *  the actual submission pipeline (lesson submit first, then report
   *  write). Throws ONLY on lesson-side failure so the modal can stay
   *  open for retry. Resolves on lesson success regardless of report
   *  success — parent handles partial-failure toast. */
  onSubmit: () => Promise<void>;
}

const HELPERS: Record<keyof ReportDraft, string> = {
  whatImproved: 'Point to what’s improved most across lessons 1 → 4.',
  keepDoing:    'Habits, mindset, drills — what’s working.',
  nextFocus:    'What you’d attack next if you had another four lessons, and what you want to see them filming.',
  personalNote: 'Encourage them, coach to player.',
};

export default function ProgressReportModal({
  playerFirstName,
  totalLessons,
  playerGoal,
  draft,
  onDraftChange,
  onClose,
  onSubmit,
}: ProgressReportModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLTextAreaElement>(null);

  const player = capName(playerFirstName);

  // Submit gate: ALL FOUR fields required (intentional 4-of-4 — see file header).
  const canSubmit =
    draft.whatImproved.trim().length > 0 &&
    draft.keepDoing.trim().length > 0 &&
    draft.nextFocus.trim().length > 0 &&
    draft.personalNote.trim().length > 0;

  const hasAnyContent =
    draft.whatImproved.trim().length > 0 ||
    draft.keepDoing.trim().length > 0 ||
    draft.nextFocus.trim().length > 0 ||
    draft.personalNote.trim().length > 0;

  // ── Close handling: confirm only when there's unsaved content ───────────────
  const requestClose = () => {
    if (submitting) return;
    if (hasAnyContent) {
      setConfirmingClose(true);
    } else {
      onClose();
    }
  };

  // Esc key closes (with the same confirm-when-content logic)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        requestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyContent, submitting]);

  // Body scroll lock — modal is portaled to document.body so the
  // background page (and the LessonVideoEditor underneath) stays put
  // while the modal is up. Restored on unmount.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  // Focus trap + initial focus on the first textarea when opened.
  useEffect(() => {
    firstFieldRef.current?.focus({ preventScroll: true });
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    dialog.addEventListener('keydown', onKeyDown);
    return () => dialog.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit();
      // Parent closes the modal on success by unmounting; no local close call.
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    }
  };

  const updateField = (key: keyof ReportDraft, value: string) =>
    onDraftChange({ ...draft, [key]: value });

  // ── Render ────────────────────────────────────────────────────────────────────
  // Portal to document.body so the modal escapes LessonVideoEditor's
  // stacking context (the editor's outer container is `position:fixed
  // z-modal`, which traps siblings — including this modal — in a context
  // that the Add Recording / ClipEditorModal overlay can paint above).
  // Inline zIndex reading the shared scale in index.css, not the `z-modal`
  // Tailwind class: `z-modal` is declared only in the dead tailwind.config.ts
  // and emits no CSS, so it resolves to `z-index: auto`.
  return createPortal(
    <div
      // Backdrop also handles outside-click close request (with confirm).
      className="fixed inset-0 flex items-stretch md:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.4)', zIndex: 'var(--z-editor-dialog)' }}
      onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="progress-report-title"
    >
      <div
        ref={dialogRef}
        // White surface, near-fullscreen on mobile, centered on desktop.
        className="w-full bg-white md:w-[720px] md:max-w-[720px] md:max-h-[92vh] flex flex-col overflow-hidden md:rounded-[16px]"
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
      >
        {/* Scrollable body — generous padding (~32px top/bottom, ~36px left/right on desktop). */}
        <div className="flex-1 overflow-y-auto px-5 py-7 md:px-9 md:py-8 text-db-text-1">

          {/* Top section: eyebrow → headline → subhead → reassurance → retention */}
          <div className="flex items-start justify-between mb-1">
            <div className="flex-1">
              {/* Eyebrow */}
              <div className="text-[11px] font-bold text-db-text-3 mb-2">
                Training Program · Lesson {totalLessons} of {totalLessons}
              </div>
              {/* Headline */}
              <h2
                id="progress-report-title"
                className="text-[24px] md:text-[26px] font-bold text-db-text-1 leading-tight mb-1.5"
              >
                {player}'s progress report
              </h2>
              {/* Subhead — folds the prior reassurance + retention lines into one. */}
              <p className="text-[15px] text-db-text-2 leading-snug">
                Helps position you to be {player}'s long term coach. Takes 5–10 minutes.
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              disabled={submitting}
              aria-label="Close progress report"
              className="ml-3 -mr-1 -mt-1 p-2 rounded-full transition-colors text-db-text-3 hover:text-db-text-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M6 6L18 18M18 6L6 18" />
              </svg>
            </button>
          </div>

          {/* Player's goal reference — muted gray, only when set.
              Sans-serif, NOT teal so it doesn't compete with the CTA. */}
          {playerGoal && playerGoal.trim().length > 0 && (
            <div className="mt-6 px-4 py-3 rounded-[10px] bg-db-bg border border-db-border">
              <div className="text-[11px] font-bold text-db-text-3 mb-1">
                Player's goal
              </div>
              <div className="text-[13px] text-db-text-1 leading-relaxed">{playerGoal}</div>
            </div>
          )}

          {/* Question stack — ~32px gap between blocks, no dividers. */}
          <div className="mt-8 space-y-8">
            {/* Q1 — What improved the most? */}
            <Question
              label="What improved the most?"
              helper={HELPERS.whatImproved}
              value={draft.whatImproved}
              onChange={(v) => updateField('whatImproved', v)}
              required
              ariaLabel="What improved the most?"
              inputRef={firstFieldRef}
            />

            {/* Q2 */}
            <Question
              label={`What should ${player} keep doing?`}
              helper={HELPERS.keepDoing}
              value={draft.keepDoing}
              onChange={(v) => updateField('keepDoing', v)}
              required
              ariaLabel={`What should ${player} keep doing?`}
            />

            {/* Q3 */}
            <Question
              label={`What would you work on next with ${player}?`}
              helper={HELPERS.nextFocus}
              value={draft.nextFocus}
              onChange={(v) => updateField('nextFocus', v)}
              required
              ariaLabel={`What would you work on next with ${player}?`}
            />

            {/* Q4 — also required (4-of-4 gate) */}
            <Question
              label={`Personal note to ${player}`}
              helper={HELPERS.personalNote}
              value={draft.personalNote}
              onChange={(v) => updateField('personalNote', v)}
              required
              ariaLabel={`Personal note to ${player}`}
            />
          </div>

          {submitError && (
            <div
              className="mt-6 px-4 py-3 rounded-lg text-[13px] bg-red-50 text-red-800 border border-red-200"
              role="alert"
            >
              {submitError}
            </div>
          )}
        </div>

        {/* Footer / CTA — Save draft on the left, primary CTA on the right. */}
        <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 md:px-9 md:py-4 border-t border-db-border bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-[13px] font-medium text-db-text-2 hover:underline underline-offset-2 disabled:opacity-40 disabled:cursor-not-allowed self-start md:self-auto"
          >
            Save draft
          </button>

          {/* Submit Report & Lesson */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="w-full md:w-auto px-6 py-3 rounded-xl text-[14px] font-bold text-white bg-biscayne hover:bg-db-navy-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed btn-press"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Submitting&hellip;</span>
              </>
            ) : (
              <span>Submit Report &amp; Lesson</span>
            )}
          </button>
        </div>
      </div>

      {/* Discard-draft confirmation — overlays the modal when X/Esc/backdrop
          click while there's content. Bumped one above the main modal's
          zIndex so it deterministically sits on top of its sibling. */}
      {confirmingClose && (
        <div
          className="fixed inset-0 flex items-center justify-center px-6"
          style={{ background: 'rgba(0,0,0,0.45)', zIndex: 'var(--z-editor-alert)' }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-[360px] bg-white rounded-[14px] p-5"
            style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
          >
            <div className="text-[16px] font-bold text-db-text-1 mb-2">
              Discard your draft?
            </div>
            <p className="text-[13px] text-db-text-2 mb-4 leading-relaxed">
              Your answers won't be saved. Use "Save draft" instead to keep them for this session.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingClose(false)}
                className="px-3 py-1.5 text-[13px] font-medium rounded-lg text-db-text-2 hover:bg-db-bg"
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingClose(false);
                  // Treat discard as a true close — parent's draft state
                  // is reset by the caller wiring (LessonVideoEditor clears
                  // its draft when the user explicitly discards).
                  onClose();
                }}
                className="px-3 py-1.5 text-[13px] font-semibold rounded-lg text-white bg-[#DC2626] hover:bg-[#B91C1C]"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface QuestionProps {
  label: string;
  helper: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  ariaLabel: string;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
}

function Question({ label, helper, value, onChange, required, ariaLabel, inputRef }: QuestionProps) {
  // Auto-resize on input — textareas grow naturally as the coach types
  // past the 96px min-height. Resets to scrollHeight on every change so
  // the field hugs its content. There is intentionally no maxLength,
  // soft cap, or length validation on these fields: coaches should be
  // able to write as much as they want.
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef ?? internalRef;

  const autoResize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  // Re-fit when the bound value changes (covers parent-driven updates like rehydration).
  useEffect(() => {
    autoResize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div>
      <label className="block">
        <div className="text-[17px] md:text-[18px] font-semibold text-db-text-1 leading-snug mb-1.5">
          {label}
          {required && (
            <span className="ml-1 align-middle text-[18px] leading-none" style={{ color: '#DC2626' }} aria-hidden="true">*</span>
          )}
        </div>
        <div className="text-[13px] text-db-text-3 mb-2.5 leading-relaxed">
          {helper}
        </div>
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => { onChange(e.target.value); autoResize(); }}
          aria-label={ariaLabel}
          className="w-full min-h-[96px] resize-none rounded-[10px] px-3.5 py-3 text-[14px] leading-relaxed text-db-text-1 placeholder:text-gray-400 bg-white border border-db-border focus:outline-none focus:border-biscayne input-glow transition-colors overflow-hidden"
        />
        <div className="mt-1 text-xs text-gray-400 text-right">2–3 sentences</div>
      </label>
    </div>
  );
}
